import { useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolStudentIntakeDetails {
  fullName?: string
  dateOfBirth?: string
  g1LicenceNumber?: string
  g1IssueDate?: string
  guardianName?: string
  guardianEmail?: string
  guardianPhone?: string
  homeAddress?: string
  homeAddressPlaceId?: string | null
  pickupAddress?: string
  pickupAddressPlaceId?: string | null
  pickupSameAsHome?: boolean
  bdeCompletedDate?: string
}

interface CreateResult {
  ok: boolean
  code?: string
  emailed?: boolean
  token?: string
}

/**
 * School-initiated student invite with the instructor pre-assigned (migration 076) — the
 * fix for the risk the product owner identified: an instructor choosing the wrong school
 * from Enroll.tsx's plain dropdown. Here the school picks the instructor at invite-creation
 * time, so the student is correctly attributed the moment they accept, with no
 * school-selection step for the instructor to get wrong at all.
 */
export function useSchoolStudentInvitations() {
  const [creating, setCreating] = useState(false)

  const createInvitation = useCallback(async (
    schoolId: string,
    instructorId: string,
    instructorName: string,
    studentEmail: string,
    details: SchoolStudentIntakeDetails,
    packageId?: string | null,
  ): Promise<CreateResult> => {
    if (creating) return { ok: false, code: 'busy' }
    setCreating(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('school-invite-student', {
        body: { schoolId, instructorId, instructorName, studentEmail, details, packageId: packageId || null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      return data as CreateResult
    } finally {
      setCreating(false)
    }
  }, [creating])

  return { creating, createInvitation }
}
